import React, { Component, useCallback, useEffect, useState } from 'react';
import Header from '../../components/Header';
import { Stack, Button, Typography, Alert } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import UserGroupTable from '../../components/tables/UserGroupTable'
import ConfirmDeleteModal from '../../components/modals/common/ConfirmDeleteModal';
import ConfirmModal from '../../components/modals/common/ConfirmModal';
import InformativeModal from '../../components/modals/common/InformativeModal';
import ViewListModal from '../../components/modals/common/ListViewModal';
import { connect } from 'react-redux';
import AuthHOC from '../Auth';
import { getAll as getAllUserGroups, remove as deleteUserGroup, patch as updateUserGroup } from '../../services/userGroupsService';
import { getAll as getUsersForUserGroup  } from '../../services/userService';

function UserGroupsView(props) {
    const [userGroups, setUserGroups] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showStatusUpdateModal, setShowStatusUpdateModal] = useState(false);
    const [statusUpdateTarget, setStatusUpdateTarget] = useState(null);
    const [showMembersModal, setShowMembersModal] = useState(null);
    const [viewMembersTarget, setViewMembersTarget] = useState(null);
    const [membersToDisplay, setMembersToDisplay] = useState(null);
    const [showCannotDeleteModal, setShowCannotDeleteModal] = useState(false);
    const [cannotDeleteInfo, setCannotDeleteInfo] = useState(null);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [warningMsg, setWarningMsg] = useState('');

    const isAdmin = props.userReducer?.userType?.toLowerCase() === 'admin';

    // Debug: Check what userType value we're getting
    console.log('UserGroupsView - userType:', props.userReducer?.userType, 'isAdmin:', isAdmin);

    function getTitle() {
      const localisation = props.localisation || {};
      return localisation["menu_header_administration_user_groups"]
          ? localisation["menu_header_administration_user_groups"].element_lang
          : "Administration - User Groups";
    };

    let title = getTitle();

    const handleUserAction = (actionKey, userGroup) => {
        const userGroupId = userGroup.id || userGroup._id;
        switch (actionKey) {
            case 'edit':
                props.history.push(`/site/administration/UserGroupDetails/${userGroupId}`);
                break;
            case 'deactivate':
                setShowStatusUpdateModal(true);
                setStatusUpdateTarget({ type: 'userGroup', entity: userGroup})                
                break;
            case 'delete':
                setShowDeleteModal(true);
                setDeleteTarget({ type: 'userGroup', entity: userGroup});
                break;            
        }
    };

    const handleMembersActionClick = async (userGroup) => {

        try {
            const result = await getUsersForUserGroup({ groupId: userGroup._id})
            if (!result.ok) {
                setError('Error loading users for a group. Error status: ' + result.status + " OK: " + result.ok);
                return;
            }
            const groupMembers = result.data.map(member => ({
                key: member._id,
                value: member.username,
                id: member.id
            }));
            setMembersToDisplay(groupMembers);

        } catch (err) {
            setError('Failed to load users for a user group. Error: ' + err.message);
        }
        finally {
            setShowMembersModal(true);
            setViewMembersTarget({entity : userGroup})
        }
    }

    const getAllGroups = useCallback(async () => {
        setIsLoading(true);
        try {
            const result = await getAllUserGroups();
            if (!result.ok) {
                setError('Error loading user groups. Error status: ' + result.status + " OK: " + result.ok);
                return;
            }
            setUserGroups(result.data || {});
            
        } catch (err) {
            setError('Error loadings user groups. Error: ' + err.message);
        }
        finally {
            setIsLoading(false);
        }
    }, []);

    const handleAddNewUserGroupClick = () => {
        props.history.push("/site/administration/UserGroupDetails");
    }

    const handleUserGroupsClick = () => {
        props.history.push("/site/administration/Users");
    }  

    useEffect(() => {
        getAllGroups();
    }, [getAllGroups]);

    const handleStatusUpdateCancelClick = () => {
        setShowStatusUpdateModal(false);
        setStatusUpdateTarget(null);
    }

    const handleCancelShowMembersModal = (value) => {
        setShowMembersModal(false);
    }

    const handleDeleteConfirm = async () => {
        if (!deleteTarget || !deleteTarget.entity) return;

        const userGroup = deleteTarget.entity;
        const userGroupId = userGroup.id || userGroup._id
        const groupName = userGroup.name || 'user group';

        try {
            const result = await deleteUserGroup(userGroupId);

            // Check if deletion failed due to group having members
            if (!result.ok && result.status === 400 && result.data?.members) {
                setCannotDeleteInfo({
                    groupName: groupName,
                    memberCount: result.data.memberCount || result.data.members.length,
                    members: result.data.members,
                    message: result.data.message || 'Cannot delete group with members',
                    details: result.data.details?.[0] || 'Please reassign members to another group first.'
                });
                setShowDeleteModal(false);
                setShowCannotDeleteModal(true);
                return;
            }

            if (!result.ok) {
                setError('Failed to delete a user group. Error status: ' + result.status + " OK: " + result.ok);
                return;
            }
            setSuccessMsg(`Successfully deleted user group: ${groupName}`);
        } catch (err) {
            setError('Failed to delete a user group. Error: ' + err.message);
        }
        finally {
            setShowDeleteModal(false);
            setDeleteTarget(null);
            getAllGroups();
        }
    }

    const handleDeleteCancel = async () => {
        setShowDeleteModal(false);
        setDeleteTarget(null);
    }

    const handleCannotDeleteModalClose = () => {
        setShowCannotDeleteModal(false);
        setCannotDeleteInfo(null);
    }

    const handleSelectedMember = (item => {
        console.log('selectd member: ', item);
        props.history.push(`/site/administration/UserDetails/${item.key}`);
    })

    const handleStatusUpdateUserClick = async () => {
        if (!statusUpdateTarget || !statusUpdateTarget.entity) return;

        const userGroup = statusUpdateTarget.entity;
        const userGroupId = userGroup.id || userGroup._id;
        try {
            const result = await updateUserGroup(userGroupId, {isActive: (userGroup.isActive ? false : true)});
            if (!result.ok) {
                setError('Failed to update a user group status. Error status: ' + result.status + " OK: " + result.ok);
                return;
            }

            setSuccessMsg('Successfully updated user group status.');
            
        } catch (err) {
            setError('Failed to update user group status. Error: ' + err.message);
        }
        finally {
            await getAllGroups();
            setShowStatusUpdateModal(false);
            setStatusUpdateTarget(null);
        }
    }

    return (
        <div className="App">
            <Header visible={!props.parsed.X3} />
            <div className="page-wrapper">
                <div className="s-page">
                    <div className="s-page-header">
                        <Stack 
                            spacing={0.5} 
                            alignItems="flex-start"
                            sx={{ mb: 3, textAlign: "left" }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                {title}
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                User Groups
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                Manage user groups and access
                            </Typography>
                        </Stack>
                         
                        <div className="s-page-actions">
                           {isAdmin && (
                                <Button
                                    variant='contained'
                                    startIcon={<AddIcon />}
                                    disableElevation
                                    type="button"
                                    sx={{ textTransform: 'none' }}
                                    onClick={handleAddNewUserGroupClick}
                                    >
                                        Add new
                                </Button>
                            )}
                            <Button
                                variant='outlined'
                                type="button"
                                sx={{
                                    textTransform: 'none',
                                    color: 'text.secondary',
                                    borderColor: 'divider',
                                    '&:hover': {
                                        color: 'primary.main',
                                        borderColor: 'primary.main'
                                    }
                                }}
                                onClick={handleUserGroupsClick}
                                >
                                    Users
                            </Button>
                        </div>
                    </div>
                     {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                        </Alert>
                    )}
                    {successMsg && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                        {successMsg}
                        </Alert>
                    )}
                    {warningMsg && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                        {warningMsg}
                        </Alert>
                    )}
                    {!isAdmin && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            You are viewing in read-only mode. Only administrators can create, edit, or delete user groups.
                        </Alert>
                    )}
                    <div className="s-content-panel">
                    <UserGroupTable
                        userGroups={userGroups}
                        isLoading={isLoading}
                        onAction={handleUserAction}
                        onMembersClickAction={handleMembersActionClick}
                        userReducer={props.userReducer}
                    />
                    </div>
                    <ConfirmDeleteModal
                        isOpen={showDeleteModal}
                        entityType={deleteTarget ? deleteTarget.type : ''}
                        entityName={
                            deleteTarget && deleteTarget.entity
                            ? (deleteTarget.entity.username || deleteTarget.entity.name)
                            : ''
                        }
                        onConfirm={handleDeleteConfirm}
                        onCancel={handleDeleteCancel}
                    />
                    <ConfirmModal
                        isOpen={showStatusUpdateModal}
                        mainTitle="Deactivate User?"
                        mainMessage="Are you sure you want to deactivate this user?"
                        textConfirm="CONFIRM"
                        textCancel="CANCEL"
                        onConfirm={handleStatusUpdateUserClick}
                        onCancel={handleStatusUpdateCancelClick}
                        >
                    </ConfirmModal>
                    <InformativeModal
                        isOpen={showCannotDeleteModal}
                        title="Cannot Delete User Group"
                        message={cannotDeleteInfo?.message || 'This group has members'}
                        details={cannotDeleteInfo?.details || 'Please reassign members to another group first.'}
                        items={cannotDeleteInfo?.members || []}
                        itemsLabel={`Members (${cannotDeleteInfo?.memberCount || 0}):`}
                        severity="error"
                        onClose={handleCannotDeleteModalClose}
                    />
                    <ViewListModal
                        isOpen={showMembersModal}
                        mainTitle='group members: '
                        entityName={viewMembersTarget ? viewMembersTarget.entity.name : ''}
                        items={membersToDisplay}
                        mainMessage='This is the list of all members from the group.'
                        onCancelText='Close'
                        onCancel={handleCancelShowMembersModal}
                        onSelect={handleSelectedMember}
                    >
                    </ViewListModal>
                </div>
            </div>
        </div>
    );
}

export default connect(function (state) { return state; })(
  AuthHOC(UserGroupsView, { name: 'administration' })
);